import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { StyledModalListItem } from '../../../components/StyledListItem';
import StyledBoardHeader from '../../../components/StyledBoardHeader';
import NoItemsInList from '../../../components/NoItemsInList';
import SearchHeader from '../../../components/SearchHeader';
import { ControlBarContext } from '../../ControlBarContext';
import Scrollable from '../../../components/Scrollable';
import useDealers from '../../../api/useDealers';
import ContentBoardStyled, {
  ModalContentBoardStyled
} from '../../../components/ContentBoardStyled';
import doFetch from '../../../api/doFetch';

import { CheckedIcon, Wrapper, RightSide } from './styledComponents';
import CreatePartnerModal from '../components/CreatePartnerModal';
import ApiClientModal from './ApiClientModal';
import PartnerClient from './PartnerClient';
import ApiKeyModal from './ApiKeyModal';
import PartnerKey from './PartnerKey';

import { VERIFICATION_FIELDS } from '../constants';

const sortSelectedDealersFirst = (dealers, partner) => {
  const partnerDealersIds = new Set(partner.dealerships.flatMap((d) => d.dealershipHmsId));
  const dealersCopy = [...dealers];
  const selectedDealers = dealersCopy
    .filter((dealer) => partnerDealersIds.has(dealer.hmsId))
    .map((d) => ({ ...d, isChecked: true }));
  const otherDealers = dealersCopy
    .filter((dealer) => !partnerDealersIds.has(dealer.hmsId))
    .map((d) => ({ ...d, isChecked: false }));
  return selectedDealers.concat(otherDealers);
};

export default function Partner({ getBreadCrumbs }) {
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(null);
  const [isAddKeyModalOpen, setIsAddKeyModalOpen] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(null);
  const [dealerInput, setDealerInput] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [partner, setPartner] = useState();

  const { setBreadCrumbs } = useContext(ControlBarContext);
  const { dealers } = useDealers(dealerInput);
  const { partnerId } = useParams();

  const reloadPartner = async () => {
    try {
      const partner = await doFetch(`/lotlenz/partners/${partnerId}`);
      setPartner(partner);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (partnerId) reloadPartner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  useEffect(() => {
    setBreadCrumbs(getBreadCrumbs(partner || null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner]);

  const boardHeaderOptions = useMemo(() => {
    const options = {
      tabs: [
        { name: 'API keys (v1)', value: 'INTERNAL' },
        { name: 'API credentials (v2)', value: 'EXTERNAL' },
        { name: 'API users (MCP)', value: 'MCP' }
      ],
      buttons: []
    };

    if (tabIndex === 0) {
      options.buttons.push({
        name: 'Generate New Key',
        handler: () => setIsAddKeyModalOpen(true)
      });
    } else {
      options.buttons.push({
        name: 'Generate New Client',
        handler: () => setIsAddClientModalOpen(tabIndex === 1 ? 'api' : 'mcp')
      });
    }

    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabIndex]);

  const handleDelete = () => {
    reloadPartner();
  };

  const onInputHandler = (event) => {
    setDealerInput(event.target.value.toLowerCase());
  };

  const onClearHandler = () => {
    setDealerInput('');
  };

  const dealersToShow = useMemo(() => {
    if (partner && dealers) {
      const sortedDealers = sortSelectedDealersFirst(dealers.items, partner);

      if (dealerInput) {
        return sortedDealers.filter((dealer) => dealer.name.toLowerCase().includes(dealerInput));
      }
      return sortedDealers;
    }

    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner, dealers, dealerInput]);

  const onChangeDealerHandler = (event) => {
    const dealerId = Number(event.currentTarget.getAttribute('data-id'));
    const partnerDealersIds = new Set(partner.dealerships.flatMap((d) => d.dealershipHmsId));
    if (partnerDealersIds.has(dealerId)) {
      partnerDealersIds.delete(dealerId);
    } else {
      partnerDealersIds.add(dealerId);
    }

    doFetch(`/lotlenz/partners/${partnerId}/dealerships`, {
      method: 'PUT',
      body: JSON.stringify({ hmsIds: Array.from(partnerDealersIds.values()) })
    }).then(() => {
      const partnerDealers = dealers.items
        .filter((d) => partnerDealersIds.has(d.hmsId))
        .map((d) => ({ dealershipHmsId: d.hmsId }));
      setPartner((partner) => ({ ...partner, dealerships: partnerDealers }));
    });
  };

  return (
    <Wrapper>
      {Boolean(isEditModalOpen) && (
        <CreatePartnerModal
          isOpen={true}
          partner={partner}
          reloadPartners={reloadPartner}
          toggleHandler={() => setIsEditModalOpen(null)}
          mode={isEditModalOpen}
        />
      )}
      {Boolean(isAddKeyModalOpen) && (
        <ApiKeyModal
          isOpen={true}
          partner={partner}
          reloadPartners={reloadPartner}
          onClose={() => setIsAddKeyModalOpen(null)}
        />
      )}
      {Boolean(isAddClientModalOpen) && (
        <ApiClientModal
          isOpen={true}
          partner={partner}
          type={isAddClientModalOpen}
          reloadPartners={reloadPartner}
          onClose={() => setIsAddClientModalOpen(null)}
        />
      )}
      <ContentBoardStyled>
        <StyledBoardHeader
          tabIndex={tabIndex}
          setTabIndex={(_, tabIndex) => setTabIndex(tabIndex)}
          {...boardHeaderOptions}
        ></StyledBoardHeader>
        <Scrollable>
          {tabIndex === 0 && (
            <div style={{ margin: 24 }}>
              {partner?.keys?.length > 0 ? (
                partner.keys
                  .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
                  .map((key) => (
                    <PartnerKey key={key.id} partnerKey={key} onDelete={handleDelete} />
                  ))
              ) : (
                <div style={{ textAlign: 'center', color: '#677b8b', padding: '20px' }}>
                  No active keys found for this partner.
                </div>
              )}
            </div>
          )}
          {tabIndex === 1 && (
            <div style={{ margin: 24 }}>
              {partner?.oauthClients?.length > 0 ? (
                partner.oauthClients
                  .filter((client) => client.type !== 'mcp')
                  .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
                  .map((client) => (
                    <PartnerClient key={client.id} client={client} onDelete={handleDelete} />
                  ))
              ) : (
                <div style={{ textAlign: 'center', color: '#677b8b', padding: '20px' }}>
                  No active credentials found for this partner.
                </div>
              )}
            </div>
          )}
          {tabIndex === 2 && (
            <div style={{ margin: 24 }}>
              {partner?.oauthClients?.length > 0 ? (
                partner.oauthClients
                  .filter((client) => client.type === 'mcp')
                  .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
                  .map((client) => (
                    <PartnerClient key={client.id} client={client} onDelete={handleDelete} />
                  ))
              ) : (
                <div style={{ textAlign: 'center', color: '#677b8b', padding: '20px' }}>
                  No active credentials found for this partner.
                </div>
              )}
            </div>
          )}
        </Scrollable>
      </ContentBoardStyled>
      <RightSide>
        <ContentBoardStyled>
          <StyledBoardHeader
            buttons={[{ name: 'Edit', handler: () => setIsEditModalOpen('GLOBAL') }]}
          >
            PARTNER INFORMATION
          </StyledBoardHeader>
          <div className='info-block content'>
            <div className='row'>
              <div className='label'>Partner name</div>
              <div className='value'>{partner?.name || ''}</div>
            </div>
            <div className='row'>
              <div className='label'>Rate limit</div>
              <div className='value'>{partner?.rateLimit || '-'}</div>
            </div>
          </div>
        </ContentBoardStyled>
        <ContentBoardStyled>
          <StyledBoardHeader
            buttons={[{ name: 'Edit', handler: () => setIsEditModalOpen('KEYS') }]}
          >
            API v1: fields
          </StyledBoardHeader>
          <div className='keys-block content'>
            <div className='row'>
              <div className='label'>KMS Signature</div>
              <div className='value'>{partner?.kmsSignatureEnabled ? 'Enabled' : '-'}</div>
            </div>
            {VERIFICATION_FIELDS.map((field) => (
              <div className='row'>
                <div className='label'>{field.name}</div>
                <div className='value'>
                  {partner?.responseTemplate?.[field.dbKey] ? 'Enabled' : '-'}
                </div>
              </div>
            ))}
          </div>
        </ContentBoardStyled>
        <ModalContentBoardStyled css={{ minHeight: 'calc(100vh - 512px)' }}>
          <StyledBoardHeader>Dealers</StyledBoardHeader>
          <SearchHeader
            userInput={dealerInput}
            onInputHandler={onInputHandler}
            placeholder='Search location'
            onClearHandler={onClearHandler}
          />
          <Scrollable maxHeight={`calc(100vh - 97px - 25px - 192px - 25px - 50px - 30px)`}>
            {dealersToShow.length ? (
              dealersToShow.map((dealer, index) => (
                <StyledModalListItem
                  key={dealer.name + index}
                  name={dealer.name}
                  css={{ cursor: 'pointer' }}
                  onClick={onChangeDealerHandler}
                  itemId={dealer.hmsId}
                >
                  {dealer.isChecked && <CheckedIcon/>}
                </StyledModalListItem>
              ))
            ) : (
              <NoItemsInList>No dealers found</NoItemsInList>
            )}
          </Scrollable>
        </ModalContentBoardStyled>
      </RightSide>
    </Wrapper>
  );
}
